import { isEqual, uniqWith } from 'lodash';
import {
  type APIReferenceType,
  type ArdoqId,
  ArrowType,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { isTruthy } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import {
  getComponentTypesForLegend,
  type GraphComponentViewLegendProps,
  type ViewLegendReferenceType,
} from '@ardoq/view-legend';

const EDGE_FALLBACK_COLOR = colors.grey400;

/** The reference type's own color, or the neutral fallback when it has none. */
export const resolveColor = (referenceType: APIReferenceType): string =>
  referenceType.color || EDGE_FALLBACK_COLOR;

const isInScope = (
  scopeComponentIds: Set<ArdoqId>,
  reference: { source: string; target: string }
): boolean =>
  scopeComponentIds.has(reference.source) &&
  scopeComponentIds.has(reference.target);

/** References whose endpoints are both in view. */
const getInScopeReferences = (
  scopeData: EnhancedScopeData,
  scopeComponentIds: Set<ArdoqId>
) =>
  scopeData.references.filter(reference =>
    isInScope(scopeComponentIds, reference)
  );

export const toLegendReferenceType = (
  referenceType: APIReferenceType
): ViewLegendReferenceType => ({
  name: referenceType.name,
  color: resolveColor(referenceType),
  line: referenceType.line,
  // A missing arrow and an explicit `NONE` render identically, so they must produce the
  // same value — otherwise the dedup key below differs and the same type reached through
  // two models fails to collapse.
  lineBeginning: referenceType.lineBeginning ?? ArrowType.NONE,
  lineEnding: referenceType.lineEnding ?? ArrowType.NONE,
});

/**
 * Legend rows in first-seen order, one per rendered row — the row itself is the
 * dedup key, so `name` counts alongside colour, line and arrowheads: a type that
 * happens to look like another still gets its own row. Keying by reference-type
 * id instead would render one identical row per model: ids are per-model
 * integers, the implicit 1/2 are reused across models, and a diagram's scope
 * data can span several.
 */
const dedupeLegendReferenceTypes = (
  referenceTypes: APIReferenceType[]
): ViewLegendReferenceType[] =>
  uniqWith(referenceTypes.map(toLegendReferenceType), isEqual);

const getInScopeReferenceTypes = (
  scopeData: EnhancedScopeData,
  scopeComponentIds: Set<ArdoqId>
): APIReferenceType[] =>
  getInScopeReferences(scopeData, scopeComponentIds)
    .map(reference =>
      enhancedScopeDataOperations.getReferenceType(scopeData, reference)
    )
    .filter(isTruthy);

/** No diagram → nothing to describe. */
export const EMPTY_LEGEND_PROPS: GraphComponentViewLegendProps = {
  displayShapesInsteadOfIcons: false,
  componentTypes: [],
  referenceTypes: [],
  activeConditionalFormatting: { components: [], references: [], tags: [] },
  activeDiffMode: null,
};

type ToLegendPropsArgs = {
  /**
   * The components whose types the legend should describe — what the builder
   * reports drawing, including component-backed group containers, so the legend
   * can't name a type that isn't on screen and doesn't miss one that is. An empty
   * list yields no component rows.
   */
  scopeComponentIds: ArdoqId[];
  /**
   * The reference types the view drew, in first-seen order. Callers pass what
   * their builder actually emitted rather than what the scope data declares:
   * a grouping rule can consume references, so a declared type may have no edge.
   */
  referenceTypes: APIReferenceType[];
  /**
   * Draw each component type as its shape rather than its icon, matching a view
   * whose components render as shapes. Views without a component-style setting
   * pass false.
   */
  displayShapesInsteadOfIcons: boolean;
};

/**
 * Component- and reference-type legend for the AI assistant views (diagram and
 * blocks). Only the type swatches are populated: there is no diff mode, and the
 * legend does not surface active conditional formatting (the views themselves do
 * apply it to node colors). Component colors are the canonical type colors,
 * matching the per-type coloring.
 */
export const toLegendProps = ({
  scopeComponentIds,
  referenceTypes,
  displayShapesInsteadOfIcons,
}: ToLegendPropsArgs): GraphComponentViewLegendProps => ({
  ...EMPTY_LEGEND_PROPS,
  displayShapesInsteadOfIcons,
  componentTypes: getComponentTypesForLegend(scopeComponentIds),
  referenceTypes: dedupeLegendReferenceTypes(referenceTypes),
});

/**
 * Legend for a view that draws straight from the scope data — Assistant Diagram
 * via `applyRecipe`. Views whose builder reports what it drew should pass that
 * to `toLegendProps` instead, so the legend can't outrun the edges.
 */
export const toScopeDataLegendProps = (
  scopeData: EnhancedScopeData
): GraphComponentViewLegendProps =>
  toLegendProps({
    scopeComponentIds: [...scopeData.scopeComponentIds],
    referenceTypes: getInScopeReferenceTypes(
      scopeData,
      scopeData.scopeComponentIds
    ),
    displayShapesInsteadOfIcons: false,
  });
